import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/site/Common";
import { DEMO_ACCOUNTS } from "@/lib/hz";
import { post, errMsg } from "@/lib/api";

export const useAuthSuccess = () => {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  return (user) => {
    qc.setQueryData(["me"], user);
    toast.success(`¡Hola, ${user.name.split(" ")[0]}!`);
    const next = sp.get("next");
    nav(next && next.startsWith("/") ? next : "/panel", { replace: true });
  };
};

export const AuthAside = () => (
  <div className="hidden flex-col justify-between rounded-3xl bg-slate-950 p-10 text-white lg:flex">
    <div className="font-heading text-xl font-extrabold">Handy<span className="text-orange-500">Zoom</span></div>
    <div>
      <ShieldCheck className="size-8 text-orange-500" />
      <div className="mt-4 font-heading text-3xl font-extrabold leading-tight">La misma cuenta en la web y en la app.</div>
      <p className="mt-3 text-slate-300">Tus trabajos, presupuestos, facturas y formación se sincronizan en directo con la app HandyZoom.</p>
    </div>
    <div className="text-xs text-slate-500">Pagos seguros con Stripe · Facturas con IVA 21%</div>
  </div>
);

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = useAuthSuccess();
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try { ok(await post("/auth/login", { email, password })); }
    catch (err) { toast.error(errMsg(err, "No se pudo iniciar sesión")); }
    finally { setBusy(false); }
  };
  return (
    <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 lg:grid-cols-2">
      <AuthAside />
      <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm md:p-10">
        <h1 className="font-heading text-3xl font-extrabold">Entrar</h1>
        <p className="mt-1 text-sm text-slate-500">Usa la misma cuenta que en la app HandyZoom.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field label="Email"><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" /></Field>
          <Field label="Contraseña"><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password-input" /></Field>
          <div className="text-right"><Link to="/recuperar" className="text-sm font-semibold text-orange-600" data-testid="forgot-link">¿Has olvidado tu contraseña?</Link></div>
          <Button type="submit" size="lg" className="w-full rounded-xl" disabled={busy} data-testid="login-submit-btn">{busy && <Loader2 className="animate-spin" />}Entrar</Button>
        </form>
        <div className="mt-8 rounded-2xl bg-slate-50 p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Cuentas de demostración</div>
          <div className="mt-3 grid gap-2">
            {DEMO_ACCOUNTS.map((a) => (
              <button key={a.id} type="button" data-testid={`demo-${a.id}`} onClick={() => { setEmail(a.email); setPassword(a.password); }}
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:border-orange-300">
                <span className="font-semibold">{a.label}</span><span className="text-xs text-slate-500">{a.email}</span>
              </button>
            ))}
          </div>
        </div>
        <p className="mt-6 text-center text-sm text-slate-600">¿No tienes cuenta? <Link to="/register" className="font-semibold text-orange-600" data-testid="go-register-link">Crear cuenta</Link></p>
      </div>
    </div>
  );
}
